import { useCallback, useState } from "react";

export const useFormState = <T>({
  initialState,
  validate = () => ({}),
  onSubmit,
  onReset,
}: {
  initialState: T;
  validate?: (state: T) => Partial<Record<keyof T, string>>;
  onSubmit: (state: T) => void | Promise<void>;
  onReset?: () => void;
}) => {
  const [state, setState] = useState<T>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const handleChange = (key: keyof T) => (value: any) => {
    setState((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = useCallback(async () => {
    const validationErrors = validate(state);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }
    await onSubmit(state);
    return true;
  }, [state, validate, onSubmit]);

  const handleReset = useCallback(
    (override?: Partial<T>) => {
      setState({ ...initialState, ...(override ?? {}) });
      if (onReset) onReset();
    },
    [initialState, onReset]
  );

  return {
    state,
    errors,
    handleChange,
    handleSubmit,
    handleReset,
  };
};
